import { useMemo } from 'react'
import { Note, isCalendarNote, filenameToDate } from '../../../utils/note'
import {
  FilterConfig,
  FilterGroup,
  FilterItem,
  GroupByOption,
  Level,
  SortConfig,
  TaskItem,
} from '../types'
import {
  extractAndGroupTasksByDate,
  extractTasksFromNote,
  formatDateForDisplay,
  sortNotesByDate,
  TASK_LIMIT,
} from '../utils'
import ItemComponent from './ItemComponent'
import {
  ItemSeparator,
  FilterHeadlineDivider,
} from '../../../components/shared/BlockRenderer'

type GroupComponentProps = {
  notes: Note[]
  filterConfig: FilterConfig
  groupBy: GroupByOption
  sortConfig: SortConfig
  taskLimitReached: boolean
  totalTaskCount: number
  taskLimit?: number
  onLoadMoreTasks?: () => void
}

export default function GroupComponent({
  notes,
  filterConfig,
  groupBy,
  sortConfig,
  taskLimitReached,
  totalTaskCount,
  taskLimit = TASK_LIMIT,
  onLoadMoreTasks,
}: GroupComponentProps) {
  // Auto-determine grouping for tasks when filtering calendar notes
  const effectiveGroupBy = useMemo(() => {
    // If level is TASKS, check for calendar notes
    if (
      filterConfig.level === Level.TASKS && // Check if calendar notes are specifically selected in filter
      filterConfig.noteType &&
      filterConfig.noteType.length > 0
    ) {
      const hasCalendarNotes = filterConfig.noteType.some((type) =>
        isCalendarNote(type)
      )
      if (hasCalendarNotes) {
        return GroupByOption.DATE
      }
    }
    // When no note type filter is set, use the default groupBy option (don't auto-group by date)
    // This preserves the original behavior of showing items in their respective notes

    // For all other cases (no calendar notes, project notes only, or other levels), use the provided groupBy option
    return groupBy
  }, [filterConfig.level, filterConfig.noteType, groupBy])

  // Generate filter groups based on grouping strategy
  const filterGroups = useMemo(() => {
    return generateFilterGroups(
      notes,
      filterConfig,
      effectiveGroupBy,
      sortConfig,
      taskLimit
    )
  }, [notes, filterConfig, effectiveGroupBy, sortConfig, taskLimit])

  // Check if we have any items to display
  const hasItems = filterGroups.some(group => group.items.length > 0)

  if (filterGroups.length === 0 || !hasItems) {
    return (
      <div className='p-4 text-center text-gray-600 dark:text-gray-400'>
        <p>
          {taskLimitReached && filterConfig.level === Level.TASKS
            ? 'Task limit reached. Refine your filters to see more specific results.'
            : 'No items match the current filters.'}
        </p>
      </div>
    )
  }

  return (
    <div className='w-full space-y-10 px-2 py-4 md:p-4'>
      {filterGroups.map((group) => (
        <div key={group.key} className='mb-6'>
          {/* Group header - show when grouping by date */}
          {effectiveGroupBy === GroupByOption.DATE && group.displayName && (
            <div className='mb-3'>
              <h4 className='mb-1 text-xs font-bold text-gray-500 no-underline dark:text-gray-400'>
                {group.displayName}
              </h4>
              <FilterHeadlineDivider />
            </div>
          )}

          {/* Items in this group */}
          <div>
            {group.items.map((item, index) => {
              // Create unique key based on item type
              const uniqueKey =
                item.type === 'task'
                  ? `task-${(item.data as TaskItem).sourceNote.recordName ?? (item.data as TaskItem).sourceNote.filename}-${String((item.data as TaskItem).lineIndex)}`
                  : `note-${(item.data as Note).recordName ?? (item.data as Note).filename}`

              return (
                <div key={uniqueKey}>
                  <ItemComponent item={item} filterConfig={filterConfig} />
                  {/* Add separator between tasks when grouped by date */}
                  {effectiveGroupBy === GroupByOption.DATE &&
                    item.type === 'task' &&
                    index < group.items.length - 1 && (
                      <ItemSeparator className='ml-6' />
                    )}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Show message if limit was reached */}
      {taskLimitReached && filterConfig.level === Level.TASKS && (
        <div className='mt-4 text-center'>
          <p className='px-4 text-sm text-gray-500 dark:text-gray-400'>
            Task limit reached. Showing first {totalTaskCount} tasks.
          </p>
          {onLoadMoreTasks && (
            <div className='mt-3 mb-8'>
              <button
                onClick={onLoadMoreTasks}
                className='px-6 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm font-medium text-sm dark:bg-amber-600 dark:hover:bg-amber-500'
              >
                Load Next 500 Tasks
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty spacer for bottom spacing */}
      <div className='h-8 md:h-12'></div>
    </div>
  )
}

// Helper function to generate filter groups based on strategy
function generateFilterGroups(
  notes: Note[],
  filterConfig: FilterConfig,
  groupBy: GroupByOption,
  sortConfig: SortConfig,
  taskLimit = TASK_LIMIT
): FilterGroup[] {
  return filterConfig.level === Level.TASKS
    ? generateTaskGroups(notes, filterConfig, groupBy, sortConfig, taskLimit)
    : generateNoteGroups(notes, groupBy, sortConfig)
}

// Generate groups for individual tasks
function generateTaskGroups(
  notes: Note[],
  filterConfig: FilterConfig,
  groupBy: GroupByOption,
  sortConfig: SortConfig,
  taskLimit = TASK_LIMIT
): FilterGroup[] {
  if (groupBy === GroupByOption.DATE) {
    // Always use task-level grouping by date to respect scheduled dates
    // This ensures scheduled tasks appear on their target dates, not the note's date

    // Sort notes by date first to ensure proper ordering
    const sortedNotes = sortNotesByDate(notes, sortConfig.direction)

    // Group by date - extract and group tasks by their scheduled dates
    const { taskGroups } = extractAndGroupTasksByDate(
      sortedNotes,
      filterConfig,
      sortConfig.direction,
      taskLimit
    )

    return taskGroups.map((taskGroup) => ({
      key: `task-group-${taskGroup.date}`,
      displayName: taskGroup.displayDate,
      items: taskGroup.tasks.map((task) => ({
        type: 'task' as const,
        data: task,
        groupKey: taskGroup.date,
        displayKey: taskGroup.displayDate,
      })),
    }))
  } else {
    // Group by note path - need to limit tasks here too
    return generateNoteGroupsWithTaskLimit(notes, groupBy, sortConfig, filterConfig, taskLimit)
  }
}

// Generate groups for notes (existing behavior)
function generateNoteGroups(
  notes: Note[],
  groupBy: GroupByOption,
  sortConfig: SortConfig
): FilterGroup[] {
  if (groupBy === GroupByOption.DATE) {
    // Sort notes by date first to ensure proper ordering
    const sortedNotes = sortNotesByDate(notes, sortConfig.direction)

    // Group notes by their calendar date
    const groupMap = new Map<string, FilterItem[]>()

    for (const note of sortedNotes) {
      let groupKey: string
      let displayKey: string

      if (isCalendarNote(note.noteType)) {
        const { date } = filenameToDate(note.filename)
        if (date) {
          groupKey = date.format('YYYY-MM-DD')
          displayKey = formatDateForDisplay(groupKey)
        } else {
          groupKey = 'unknown'
          displayKey = 'Unknown Date'
        }
      } else {
        groupKey = 'no-date'
        displayKey = 'No Date'
      }

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, [])
      }
      groupMap.get(groupKey)?.push({
        type: 'note',
        data: note,
        groupKey,
        displayKey,
      })
    }

    return [...groupMap.entries()]
      .map(([key, items]) => ({
        key: `note-date-group-${key}`,
        displayName: items[0].displayKey,
        items,
      }))
      .sort((a, b) =>
        sortConfig.direction === 'desc'
          ? b.key.localeCompare(a.key)
          : a.key.localeCompare(b.key)
      )
  } else {
    // For note path grouping, sort by note title/filename alphabetically
    // This gives predictable ordering for regular notes without meaningful dates
    const sortedNotes = [...notes].sort((a, b) => {
      const titleA = a.title ?? a.filename
      const titleB = b.title ?? b.filename
      const comparison = titleA.localeCompare(titleB)
      return sortConfig.direction === 'desc' ? -comparison : comparison
    })

    // Group by note path - for tasks level, show individual notes
    // For notes level, could be grouped differently but for now show individual notes
    return sortedNotes.map((note, index) => ({
      key: `note-path-group-${note.recordName ?? note.filename}-${String(index)}`,
      displayName: '', // Will be handled by the note component
      items: [
        {
          type: 'note' as const,
          data: note,
          groupKey: note.recordName ?? note.filename,
          displayKey: getNotePath(note),
        },
      ],
    }))
  }
}

// Helper function to get note path for grouping
function getNotePath(note: Note): string {
  // Use the note's title or filename
  return note.title ?? note.filename
}

// Generate note groups with task limit for task level
function generateNoteGroupsWithTaskLimit(
  notes: Note[],
  _groupBy: GroupByOption,
  sortConfig: SortConfig,
  filterConfig: FilterConfig,
  taskLimit = TASK_LIMIT
): FilterGroup[] {
  let totalTasksCollected = 0
  
  // Sort notes
  const sortedNotes = [...notes].sort((a, b) => {
    const titleA = a.title ?? a.filename
    const titleB = b.title ?? b.filename
    const comparison = titleA.localeCompare(titleB)
    return sortConfig.direction === 'desc' ? -comparison : comparison
  })
  
  const groups: FilterGroup[] = []
  
  for (const [index, note] of sortedNotes.entries()) {
    // Stop processing if we've hit the limit
    if (totalTasksCollected >= taskLimit) {
      break
    }
    
    // Extract tasks from this note
    const tasks = extractTasksFromNote(note, filterConfig)
    
    // Limit tasks to remaining capacity
    const remainingCapacity = taskLimit - totalTasksCollected
    const tasksToInclude = tasks.slice(0, remainingCapacity)
    
    // Only include the note if it has tasks
    if (tasksToInclude.length > 0) {
      // For note groups showing tasks, we need to create a custom note object
      // that will only render the limited tasks
      const limitedNote = {
        ...note,
        _limitedTasks: tasksToInclude, // Special property to indicate limited tasks
      }
      
      groups.push({
        key: `note-path-group-${note.recordName ?? note.filename}-${String(index)}`,
        displayName: '', // Will be handled by the note component
        items: [
          {
            type: 'note' as const,
            data: limitedNote,
            groupKey: note.recordName ?? note.filename,
            displayKey: getNotePath(note),
          },
        ],
      })
      
      totalTasksCollected += tasksToInclude.length
    }
  }
  
  return groups
}
